import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuthHook';
import { useAuthorization } from '@/contexts/AuthorizationContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { 
  FileText, 
  CheckCircle, 
  Clock,
  DollarSign,
  Calculator,
  Loader2,
  AlertCircle,
  User,
  Zap,
  Package,
  TrendingUp,
  BarChart3
} from 'lucide-react';

interface PropostaAguardandoRevisao {
  id: string;
  lead_id: string;
  lead_nome: string;
  vendedor_nome: string;
  parceiro_nome: string;
  data_envio: string;
  valor_total_kits: number;
  potencia_total_paineis: number;
  dimensionamento_id: string;
}

interface DadosEtapa1 {
  nome: string | null;
  razao_social: string | null;
  cpf: string | null;
  cnpj: string | null;
  representante_legal: string | null;
  email: string | null;
  telefone: string | null;
  endereco: string | null;
  numero: string | null;
  cidade: string | null;
  estado: string | null;
  cep: string | null;
  tipo: string | null;
  origem: string | null;
  tipo_fixacao: string | null;
  tipo_telha: string | null;
  consumo_mensal: number | null;
  grupo_tarifario: string | null;
  padrao: string | null;
  parceiro_id?: string | null;
  parceiro_pai_id?: string | null;
}

interface DadosEtapa2 {
  dimensio: number | null;
  eficiencia: number | null;
  geracao_anual_kwp: number | null;
  percentual: number | null;
  dados_mensais: any[] | null;
  geracao_mensal: any[] | null;
}

interface KitSelecionado {
  codigo: string | null;
  nome: string | null;
  potencia: number | null;
  preco: number | null;
  quantidade: number | null;
}

interface DadosEtapa3 {
  kits: KitSelecionado[];
  valor_total_kits: number;
  potencia_total_paineis: number;
  total_paineis: number;
  potencia_total_inversores: number;
  kits_r_wp: number;
}

interface DadosPrecificacao {
  custo_materiais: number;
  impostos_percentual: number;
  mao_obra_por_placa: number;
  valor_total_kits: number;
  potencia_total_paineis: number;
  total_paineis: number;
  comissao_vendedor: number;
  comissao_parceiro: number;
  comissao_parceiro_pai: number;
  integracao_r_wp: number;
  incluir_custos_estrutura_fundacao?: boolean;
  valor_estrutura_metallica: number;
  valor_fundacao: number;
  valor_equipamentos_alugados: number;
}

interface DadosConsumo {
  consumo_anual: number | null;
  despesa_energia_anual: number | null;
  despesa_cosip_anual: number | null;
  despesa_fatura_anual: number | null;
}

interface DadosCompletos {
  etapa1: DadosEtapa1 | null;
  etapa2: DadosEtapa2 | null;
  etapa3: DadosEtapa3 | null;
  etapa4: DadosPrecificacao | null;
  dadosConsumo: DadosConsumo | null;
}

export default function RevisaoPrecificacaoAdmin() {
  const [propostas, setPropostas] = useState<PropostaAguardandoRevisao[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [propostaSelecionada, setPropostaSelecionada] = useState<PropostaAguardandoRevisao | null>(null);
  const [dadosCompletos, setDadosCompletos] = useState<DadosCompletos | null>(null);
  const [isAprovando, setIsAprovando] = useState(false);
  const [isLoadingDados, setIsLoadingDados] = useState(false);
  
  // Campos editáveis pelo admin
  const [custoMateriais, setCustoMateriais] = useState<string>('0');
  const [impostosPercentual, setImpostosPercentual] = useState<string>('12');
  const [maoObraPorPlaca, setMaoObraPorPlaca] = useState<string>('0');
  
  // Novos campos editáveis pelo admin (valores padrão serão definidos baseados no lead ao carregar)
  const [comissaoVendedor, setComissaoVendedor] = useState<string>('0');
  const [comissaoParceiro, setComissaoParceiro] = useState<string>('5');
  const [comissaoParceiroPai, setComissaoParceiroPai] = useState<string>('0');
  const [integracaoRwp, setIntegracaoRwp] = useState<string>('1');
  
  // Campos de estrutura de solo editáveis pelo admin
  const [valorEstruturaMetallica, setValorEstruturaMetallica] = useState<string>('0');
  const [valorFundacao, setValorFundacao] = useState<string>('0');
  const [valorEquipamentosAlugados, setValorEquipamentosAlugados] = useState<string>('0');
  const [incluirCustosEstruturaFundacao, setIncluirCustosEstruturaFundacao] = useState(true);
  
  const { toast } = useToast();
  const { user } = useAuth();
  const { hasRole, userProfile } = useAuthorization();
  const isMobile = useIsMobile();
  
  // Verificar se o usuário é admin ou vendedor
  const isAdmin = hasRole('admin');
  const isVendedor = hasRole('vendedor');

  // Função para formatar telefone
  const formatarTelefone = (telefone: string | null | undefined) => {
    if (!telefone) return '-';
    
    // Remove todos os caracteres não numéricos
    const numeros = telefone.replace(/\D/g, '');
    
    // Se tem 11 dígitos (celular): (00) 00000-0000
    if (numeros.length === 11) {
      return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`;
    }
    
    // Se tem 10 dígitos (fixo): (00) 0000-0000
    if (numeros.length === 10) {
      return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 6)}-${numeros.slice(6)}`;
    }
    
    // Se não tem 10 ou 11 dígitos, retorna como está
    return telefone;
  };

  // Carregar propostas aguardando revisão
  const loadPropostas = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Buscar propostas aguardando revisão com informações do lead, vendedor e parceiro
      // @ts-ignore - Type instantiation is excessively deep warning devido a query aninhada complexa
      let query = supabase
        .from('propostas_clientes')
        .select(`
          id,
          lead_id,
          valor_total_kits,
          potencia_total_paineis,
          dimensionamento_id,
          created_at,
          leads!inner (
            nome,
            razao_social,
            vendedor_id,
            parceiro_id,
            vendedor:profiles!leads_vendedor_id_fkey (
              nome
            ),
            parceiro:profiles!leads_parceiro_id_fkey (
              nome
            )
          )
        `)
        .eq('status_revisao', 'aguardando_revisao');

      // Se for vendedor, filtrar apenas suas propostas
      if (isVendedor && user?.id) {
        query = query.eq('leads.vendedor_id', user.id);
      }

      const response = await query;

      if (response.error) throw response.error;

      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        // Mapear os dados para o formato esperado
        const propostasFormatadas = response.data.map((proposta: any) => ({
          id: proposta.id,
          lead_id: proposta.lead_id,
          lead_nome: proposta.leads?.nome || proposta.leads?.razao_social || 'N/A',
          vendedor_nome: proposta.leads?.vendedor?.nome || 'Não atribuído',
          parceiro_nome: proposta.leads?.parceiro?.nome || 'Não atribuído',
          data_envio: proposta.created_at,
          valor_total_kits: proposta.valor_total_kits || 0,
          potencia_total_paineis: proposta.potencia_total_paineis || 0,
          dimensionamento_id: proposta.dimensionamento_id
        }));
        setPropostas(propostasFormatadas);
      } else {
        setPropostas([]);
      }

    } catch (error) {
      console.error('Erro ao carregar propostas:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar propostas aguardando revisão.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, isVendedor, user?.id]);

  useEffect(() => {
    loadPropostas();
  }, [loadPropostas]);

  // Abrir dialog de revisão e buscar todos os dados das 4 etapas
  const handleRevisar = async (proposta: PropostaAguardandoRevisao) => {
    try {
      setIsLoadingDados(true);
      setPropostaSelecionada(proposta);
      setDialogOpen(true);

      console.log('🔍 Buscando dados completos para proposta:', proposta.id);
      console.log('📋 Lead ID:', proposta.lead_id);
      console.log('📋 Dimensionamento ID:', proposta.dimensionamento_id);

      // ETAPA 1: Buscar dados do lead
      const { data: leadDataRaw, error: leadError } = await supabase
        .from('leads')
        .select(`
          nome,
          razao_social,
          cpf,
          cnpj,
          representante_legal,
          email,
          telefone,
          endereco,
          numero,
          cidade,
          estado,
          cep,
          tipo,
          origem,
          tipo_fixacao,
          tipo_telha,
          consumo_mensal,
          grupo_tarifario,
          padrao,
          parceiro_id,
          parceiro:profiles!leads_parceiro_id_fkey (
            parceiro_cadastrado_por
          )
        `)
        .eq('id', proposta.lead_id)
        .single();

      if (leadError) {
        console.error('Erro ao carregar dados do lead:', leadError);
      }

      const leadData = leadDataRaw ? {
        ...leadDataRaw,
        parceiro_pai_id: (leadDataRaw as any).parceiro?.parceiro_cadastrado_por
      } : null;

      // Buscar dados de consumo da tabela clientes_grupob
      const { data: consumoData, error: consumoError } = await supabase
        .from('clientes_grupob')
        .select('consumo_anual, despesa_energia_anual, despesa_cosip_anual, despesa_fatura_anual')
        .eq('lead_id', proposta.lead_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (consumoError) {
        console.error('Erro ao carregar dados de consumo:', consumoError);
      }

      // ETAPA 2: Buscar dados do dimensionamento da tabela clientes_grupob
      const { data: dimensionamentoData, error: dimError } = await supabase
        .from('clientes_grupob')
        .select(`
          dimensio,
          eficiencia,
          geracao_anual_kwp,
          percentual,
          dados_mensais,
          geracao_mensal
        `)
        .eq('lead_id', proposta.lead_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (dimError) {
        console.error('Erro ao carregar dados do dimensionamento:', dimError);
      }

      // ETAPA 3: Buscar dados dos kits da tabela propostas_clientes
      const { data: kitsData, error: kitsError } = await supabase
        .from('propostas_clientes')
        .select(`
          kit1_codigo,
          kit1_nome,
          kit1_potencia,
          kit1_preco,
          kit1_quantidade,
          kit2_codigo,
          kit2_nome,
          kit2_potencia,
          kit2_preco,
          kit2_quantidade,
          kit3_codigo,
          kit3_nome,
          kit3_potencia,
          kit3_preco,
          kit3_quantidade,
          kit4_codigo,
          kit4_nome,
          kit4_potencia,
          kit4_preco,
          kit4_quantidade
        `)
        .eq('id', proposta.id)
        .single();

      if (kitsError) {
        console.error('Erro ao carregar kits:', kitsError);
      }

      // ETAPA 4: Buscar dados de precificação
      const { data: precificacaoData, error: precError } = await supabase
        .from('propostas_clientes')
        .select(`
          custo_materiais,
          impostos_percentual,
          mao_obra_por_placa,
          valor_total_kits,
          potencia_total_paineis,
          total_paineis,
          comissao_vendedor,
          comissao_parceiro,
          comissao_parceiro_pai,
          integracao_r_wp,
          incluir_custos_estrutura_fundacao,
          valor_estrutura_metallica,
          valor_fundacao,
          valor_equipamentos_alugados,
          kits_r_wp,
          potencia_total_inversores,
          preco_projeto
        `)
        .eq('id', proposta.id)
        .single();

      if (precError) throw precError;

      // Processar kits dos dados da proposta
      const kits: KitSelecionado[] = [];
      if (kitsData) {
        // Kit 1
        if (kitsData.kit1_codigo && kitsData.kit1_nome) {
          kits.push({
            codigo: String(kitsData.kit1_codigo),
            nome: kitsData.kit1_nome,
            potencia: kitsData.kit1_potencia,
            preco: kitsData.kit1_preco,
            quantidade: kitsData.kit1_quantidade
          });
        }
        // Kit 2
        if (kitsData.kit2_codigo && kitsData.kit2_nome) {
          kits.push({
            codigo: String(kitsData.kit2_codigo),
            nome: kitsData.kit2_nome,
            potencia: kitsData.kit2_potencia,
            preco: kitsData.kit2_preco,
            quantidade: kitsData.kit2_quantidade
          });
        }
        // Kit 3
        if (kitsData.kit3_codigo && kitsData.kit3_nome) {
          kits.push({
            codigo: String(kitsData.kit3_codigo),
            nome: kitsData.kit3_nome,
            potencia: kitsData.kit3_potencia,
            preco: kitsData.kit3_preco,
            quantidade: kitsData.kit3_quantidade
          });
        }
        // Kit 4
        if (kitsData.kit4_codigo && kitsData.kit4_nome) {
          kits.push({
            codigo: String(kitsData.kit4_codigo),
            nome: kitsData.kit4_nome,
            potencia: kitsData.kit4_potencia,
            preco: kitsData.kit4_preco,
            quantidade: kitsData.kit4_quantidade
          });
        }
      }

      // Montar objeto com todos os dados
      const dadosCompletos: DadosCompletos = {
        etapa1: leadData as DadosEtapa1,
        etapa2: dimensionamentoData as DadosEtapa2,
        etapa3: {
          kits: kits,
          valor_total_kits: precificacaoData.valor_total_kits,
          potencia_total_paineis: precificacaoData.potencia_total_paineis,
          total_paineis: precificacaoData.total_paineis,
          potencia_total_inversores: precificacaoData.potencia_total_inversores,
          kits_r_wp: precificacaoData.kits_r_wp
        },
        etapa4: precificacaoData as DadosPrecificacao,
        dadosConsumo: consumoData as DadosConsumo
      };

      console.log('✅ Dados completos carregados:', dadosCompletos);
      console.log('🔍 DEBUG - Estrutura dos dados:');
      console.log('  dadosCompletos.etapa1:', dadosCompletos.etapa1);
      console.log('  dadosCompletos.etapa4:', dadosCompletos.etapa4);

      setDadosCompletos(dadosCompletos);
      
      // Calcular valores padrão se não existirem (mesma lógica da etapa 4)
      const totalPaineis = precificacaoData.total_paineis || 0;
      
      // Custo de materiais: R$ 60 por placa, mínimo R$ 350
      // Sempre recalcular com o padrão atual (parceiros podem ter enviado com o valor antigo)
      const custoMateriaisCalculado = Math.max(60 * totalPaineis, 350);
      
      // Mão de obra por placa: R$ 100 para até 25 painéis, R$ 70 para mais de 25
      const maoObraPorPlacaCalculado = precificacaoData.mao_obra_por_placa && precificacaoData.mao_obra_por_placa > 0
        ? precificacaoData.mao_obra_por_placa
        : (totalPaineis <= 25 ? 100 : 70);
      
      // Preencher valores iniciais dos campos editáveis
      setCustoMateriais(custoMateriaisCalculado.toString());
      setImpostosPercentual(precificacaoData.impostos_percentual?.toString() || '12');
      setMaoObraPorPlaca(maoObraPorPlacaCalculado.toString());
      
      // Preencher novos campos editáveis
      // Calcular valores padrão baseado na presença de parceiro
      const hasParceiro = leadData?.parceiro_id !== null && leadData?.parceiro_id !== undefined && leadData?.parceiro_id !== '';
      const hasParceiroPai = leadData?.parceiro_pai_id !== null && leadData?.parceiro_pai_id !== undefined && leadData?.parceiro_pai_id !== '';
      
      let defaultComissaoVendedor = 0;
      let defaultComissaoParceiro = hasParceiro ? 5 : 0;
      let defaultComissaoParceiroPai = 0;

      if (hasParceiro && hasParceiroPai) {
        defaultComissaoVendedor = 0;
        defaultComissaoParceiro = 5;
        defaultComissaoParceiroPai = 1;
      }

      // Usar sempre os novos valores pré-estabelecidos na revisão admin
      // (propostas de parceiros/vendedores podem ter sido salvas com defaults antigos)
      setComissaoVendedor(defaultComissaoVendedor.toString());
      setComissaoParceiro(defaultComissaoParceiro.toString());
      setComissaoParceiroPai(defaultComissaoParceiroPai.toString());
      setIntegracaoRwp('1');
      
      // Calcular e preencher valores iniciais dos campos de estrutura de solo/carport
      const tipoFixacao = leadData?.tipo_fixacao;
      const incluirCustosDb = precificacaoData.incluir_custos_estrutura_fundacao !== false;
      setIncluirCustosEstruturaFundacao(incluirCustosDb);

      console.log('🔍 DEBUG - Dados para cálculo:');
      console.log('  tipoFixacao:', tipoFixacao);
      console.log('  totalPaineis:', totalPaineis);
      console.log('  precificacaoData.valor_estrutura_metallica:', precificacaoData.valor_estrutura_metallica);
      console.log('  precificacaoData.valor_fundacao:', precificacaoData.valor_fundacao);
      console.log('  precificacaoData.valor_equipamentos_alugados:', precificacaoData.valor_equipamentos_alugados);
      
      let valorEstruturaInicial = 0;
      let valorFundacaoInicial = 0;
      let valorEquipamentosInicial = 0;
      
      // Aplicar a mesma lógica do dimensionamento
      if (tipoFixacao === 'Solo') {
        // Estrutura Metálica: R$ 220,00 por painel
        valorEstruturaInicial = 220 * totalPaineis;
        // Fundação: R$ 15,00 por painel
        valorFundacaoInicial = 15 * totalPaineis;
        // Equipamentos Alugados: R$ 14,00 por painel (mínimo R$ 500,00)
        valorEquipamentosInicial = Math.max(14 * totalPaineis, 500);
        console.log('🔧 DEBUG - Calculado para Solo:', { valorEstruturaInicial, valorFundacaoInicial, valorEquipamentosInicial });
      } else if (tipoFixacao === 'Carport') {
        // Estrutura Metálica: R$ 500,00 por painel
        valorEstruturaInicial = 500 * totalPaineis;
        // Fundação: R$ 22,00 por painel (mínimo R$ 2.000,00)
        valorFundacaoInicial = Math.max(22 * totalPaineis, 2000);
        // Equipamentos Alugados: R$ 14,00 por painel (mínimo R$ 500,00)
        valorEquipamentosInicial = Math.max(14 * totalPaineis, 500);
        console.log('🔧 DEBUG - Calculado para Carport:', { valorEstruturaInicial, valorFundacaoInicial, valorEquipamentosInicial });
      } else {
        // TEMPORÁRIO: Para debug, calcular valores padrão mesmo se não for Solo/Carport
        console.log('🔧 DEBUG - Tipo de fixação não é Solo/Carport, calculando valores padrão para teste');
        valorEstruturaInicial = 220 * totalPaineis;
        valorFundacaoInicial = 15 * totalPaineis;
        valorEquipamentosInicial = Math.max(14 * totalPaineis, 500);
        console.log('🔧 DEBUG - Valores calculados para teste:', { valorEstruturaInicial, valorFundacaoInicial, valorEquipamentosInicial });
      }
      
      if (tipoFixacao === 'Solo' && !incluirCustosDb) {
        setValorEstruturaMetallica('0');
        setValorFundacao('0');
        setValorEquipamentosAlugados('0');
      } else {
        // Para campos de estrutura de solo/carport, usar valores salvos ou calculados
        const valorEstruturaFinal =
          precificacaoData.valor_estrutura_metallica && precificacaoData.valor_estrutura_metallica > 0
            ? precificacaoData.valor_estrutura_metallica
            : valorEstruturaInicial;
        const valorFundacaoFinal =
          precificacaoData.valor_fundacao && precificacaoData.valor_fundacao > 0
            ? precificacaoData.valor_fundacao
            : valorFundacaoInicial;
        const valorEquipamentosFinal =
          precificacaoData.valor_equipamentos_alugados && precificacaoData.valor_equipamentos_alugados > 0
            ? precificacaoData.valor_equipamentos_alugados
            : valorEquipamentosInicial;

        console.log('🔧 DEBUG - Valores finais a serem definidos:', {
          valorEstruturaFinal,
          valorFundacaoFinal,
          valorEquipamentosFinal,
        });

        setValorEstruturaMetallica(valorEstruturaFinal.toString());
        setValorFundacao(valorFundacaoFinal.toString());
        setValorEquipamentosAlugados(valorEquipamentosFinal.toString());
      }

    } catch (error) {
      console.error('Erro ao carregar dados da proposta:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar dados da proposta.',
        variant: 'destructive'
      });
      setDialogOpen(false);
    } finally {
      setIsLoadingDados(false);
    }
  };

  // Aprovar revisão
  const handleAprovar = async () => {
    if (!propostaSelecionada || !user) {
      return;
    }

    try {
      setIsAprovando(true);

      // Validar campos
      const custo = parseFloat(custoMateriais.replace(',', '.')) || 0;
      const impostos = parseFloat(impostosPercentual.replace(',', '.')) || 12;
      const maoObra = parseFloat(maoObraPorPlaca.replace(',', '.')) || 0;
      
      // Validar novos campos
      const comissaoV = parseFloat(comissaoVendedor.replace(',', '.')) || 0;
      const comissaoP = parseFloat(comissaoParceiro.replace(',', '.')) || 0;
      const comissaoPP = parseFloat(comissaoParceiroPai.replace(',', '.')) || 0;
      const integracao = parseFloat(integracaoRwp.replace(',', '.')) || 1;
      
      const tipoFixacaoAprovacao = dadosCompletos?.etapa1?.tipo_fixacao;
      const isSoloAprovacao = tipoFixacaoAprovacao === 'Solo';
      const isCarportAprovacao = tipoFixacaoAprovacao === 'Carport';
      const isCarportOrSolo = isCarportAprovacao || isSoloAprovacao;
      const usarCustosEstruturaFundacao = isCarportAprovacao || (isSoloAprovacao && incluirCustosEstruturaFundacao);

      const valorEstruturaAprovacao = usarCustosEstruturaFundacao
        ? parseFloat(valorEstruturaMetallica.replace(',', '.')) || 0
        : 0;
      const valorFundacaoAprovacao = usarCustosEstruturaFundacao
        ? parseFloat(valorFundacao.replace(',', '.')) || 0
        : 0;
      const valorEquipamentosAprovacao = usarCustosEstruturaFundacao
        ? parseFloat(valorEquipamentosAlugados.replace(',', '.')) || 0
        : 0;

      if (custo < 0 || impostos < 0 || maoObra < 0 || valorEstruturaAprovacao < 0 || valorFundacaoAprovacao < 0 || valorEquipamentosAprovacao < 0 || comissaoV < 0 || comissaoP < 0 || comissaoPP < 0 || integracao < 0) {
        toast({
          title: 'Erro de Validação',
          description: 'Os valores não podem ser negativos.',
          variant: 'destructive'
        });
        return;
      }

      const etapa4 = dadosCompletos?.etapa4;
      if (!etapa4) {
        toast({
          title: 'Erro',
          description: 'Dados da precificação incompletos. Recarregue a proposta e tente novamente.',
          variant: 'destructive'
        });
        return;
      }

      const potenciaTotalPaineis = Number(etapa4.potencia_total_paineis) || 0;
      const totalPaineis = Number(etapa4.total_paineis) || 0;
      const valorTotalKits = Number(etapa4.valor_total_kits) || 0;

      const preco_integracao = integracao * potenciaTotalPaineis * 1000;
      const preco_projeto = valorTotalKits + valorEstruturaAprovacao + preco_integracao;
      const impostos_valor = (impostos / 100) * preco_integracao;
      const mao_obra_total = maoObra * totalPaineis;
      const valor_base_comissao = isCarportOrSolo
        ? (preco_projeto - valorEstruturaAprovacao)
        : preco_projeto;
      const comissao_vendedor_valor = (comissaoV / 100) * valor_base_comissao;
      const comissao_parceiro_valor = (comissaoP / 100) * valor_base_comissao;
      const comissao_parceiro_pai_valor = (comissaoPP / 100) * valor_base_comissao;
      const custo_total_integracao =
        custo + mao_obra_total + impostos_valor + valorFundacaoAprovacao + valorEquipamentosAprovacao;
      const custo_total_implantacao =
        valorTotalKits +
        valorEstruturaAprovacao +
        valorFundacaoAprovacao +
        mao_obra_total +
        valorEquipamentosAprovacao +
        custo +
        impostos_valor +
        comissao_vendedor_valor +
        comissao_parceiro_valor +
        comissao_parceiro_pai_valor;
      const lucro_liquido =
        preco_integracao -
        custo_total_integracao -
        comissao_vendedor_valor -
        comissao_parceiro_valor -
        comissao_parceiro_pai_valor -
        impostos_valor;
      const lucro_liquido_percentual =
        preco_integracao > 0 ? (lucro_liquido / preco_integracao) * 100 : 0;
      const preco_por_wp =
        potenciaTotalPaineis > 0 ? preco_projeto / (potenciaTotalPaineis * 1000) : 0;

      // Atualizar proposta com campos do admin + totais calculados (necessários para projeção)
      const { error: updateError } = await supabase
        .from('propostas_clientes')
        .update({ 
          status_revisao: 'revisada',
          dimensionamento_finalizado: true,
          data_conclusao_revisao: new Date().toISOString(),
          revisado_por: user.id,
          custo_materiais: custo,
          impostos_percentual: impostos,
          mao_obra_por_placa: maoObra,
          comissao_vendedor: comissaoV,
          comissao_parceiro: comissaoP,
          comissao_parceiro_pai: comissaoPP,
          comissao_vendedor_valor,
          comissao_parceiro_valor,
          comissao_parceiro_pai_valor,
          integracao_r_wp: integracao,
          incluir_custos_estrutura_fundacao: !isSoloAprovacao || incluirCustosEstruturaFundacao,
          valor_estrutura_metallica: valorEstruturaAprovacao,
          valor_fundacao: valorFundacaoAprovacao,
          valor_equipamentos_alugados: valorEquipamentosAprovacao,
          preco_projeto,
          preco_integracao,
          impostos: impostos_valor,
          mao_obra: mao_obra_total,
          custo_total_implantacao,
          custo_total_integracao,
          preco_por_wp_final: preco_por_wp,
          lucro_liquido,
          lucro_liquido_percentual,
          campos_admin_preenchidos: {
            custo_materiais: custo,
            impostos_percentual: impostos,
            mao_obra_por_placa: maoObra,
            comissao_vendedor: comissaoV,
            comissao_parceiro: comissaoP,
            comissao_parceiro_pai: comissaoPP,
            integracao_r_wp: integracao,
          },
        })
        .eq('id', propostaSelecionada.id);

      if (updateError) throw updateError;

      toast({
        title: 'Revisão Aprovada',
        description: 'Dimensionamento aprovado. O parceiro já pode criar a proposta na página de Propostas.',
        variant: 'default'
      });

      // Fechar dialog e recarregar lista
      setDialogOpen(false);
      setPropostaSelecionada(null);
      setDadosCompletos(null);
      loadPropostas();

    } catch (error) {
      console.error('Erro ao aprovar revisão:', error);
      toast({
        title: 'Erro',
        description: error instanceof Error ? error.message : 'Erro ao aprovar revisão.',
        variant: 'destructive'
      });
    } finally {
      setIsAprovando(false);
    }
  };

  const calcularPreview = () => {
    if (!dadosCompletos?.etapa4) return null;

    const etapa4 = dadosCompletos.etapa4;
    const custo = parseFloat(custoMateriais.replace(',', '.')) || 0;
    const impostos = parseFloat(impostosPercentual.replace(',', '.')) || 12;
    const maoObra = parseFloat(maoObraPorPlaca.replace(',', '.')) || 0;
    
    // Novos campos editáveis
    const comissaoV = parseFloat(comissaoVendedor.replace(',', '.')) || 0;
    const comissaoP = parseFloat(comissaoParceiro.replace(',', '.')) || 0;
    const comissaoPP = parseFloat(comissaoParceiroPai.replace(',', '.')) || 0;
    const integracaoRwpValue = parseFloat(integracaoRwp.replace(',', '.')) || 1;
    
    const tipoFixacaoPreview = dadosCompletos.etapa1?.tipo_fixacao;
    const isSoloPreview = tipoFixacaoPreview === 'Solo';
    const isCarportPreview = tipoFixacaoPreview === 'Carport';
    const isCarportOrSolo = isCarportPreview || isSoloPreview;
    const usarCustosEstruturaPreview = isCarportPreview || (isSoloPreview && incluirCustosEstruturaFundacao);

    const valorEstrutura = usarCustosEstruturaPreview
      ? parseFloat(valorEstruturaMetallica.replace(',', '.')) || 0
      : 0;
    const valorFundacaoCalculado = usarCustosEstruturaPreview
      ? parseFloat(valorFundacao.replace(',', '.')) || 0
      : 0;
    const valorEquipamentos = usarCustosEstruturaPreview
      ? parseFloat(valorEquipamentosAlugados.replace(',', '.')) || 0
      : 0;

    const preco_integracao = integracaoRwpValue * etapa4.potencia_total_paineis * 1000;
    const preco_projeto = etapa4.valor_total_kits + valorEstrutura + preco_integracao;
    
    const impostos_valor = (impostos / 100) * preco_integracao;
    const mao_obra_total = maoObra * etapa4.total_paineis;

    const valor_base_comissao = isCarportOrSolo ? (preco_projeto - valorEstrutura) : preco_projeto;
    const comissao_vendedor_valor = (comissaoV / 100) * valor_base_comissao;
    const comissao_parceiro_valor = (comissaoP / 100) * valor_base_comissao;
    const comissao_parceiro_pai_valor = (comissaoPP / 100) * valor_base_comissao;
    const comissao_total = comissao_vendedor_valor + comissao_parceiro_valor + comissao_parceiro_pai_valor;

    const custo_total_integracao = custo + mao_obra_total + impostos_valor + valorFundacaoCalculado + valorEquipamentos;
    const lucro_liquido = preco_integracao - custo_total_integracao - comissao_total;
    const lucro_percentual = preco_integracao > 0 ? (lucro_liquido / preco_integracao) * 100 : 0;

    return {
      preco_projeto,
      preco_integracao,
      impostos_valor,
      mao_obra_total,
      custo_total_integracao,
      lucro_liquido,
      lucro_percentual
    };
  };

  const preview = calcularPreview();

  return (
    <div className={`space-y-6 ${isMobile ? 'p-4' : 'p-6'}`}>
      <div className={`flex ${isMobile ? 'flex-col gap-4' : 'items-center justify-between'}`}>
        <div>
          <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-foreground`}>
            {isAdmin ? 'Revisão de Precificação' : 'Dimensionamentos Pendentes'}
          </h1>
          <p className={`text-muted-foreground ${isMobile ? 'mt-1 text-sm' : 'mt-2'}`}>
            {isAdmin 
              ? 'Propostas enviadas por vendedores aguardando aprovação'
              : 'Seus dimensionamentos aguardando revisão do administrador'}
          </p>
        </div>
        <Badge variant="secondary" className={`${isMobile ? 'text-base self-start' : 'text-lg'} px-4 py-2`}>
          {propostas.length} {propostas.length === 1 ? 'proposta' : 'propostas'}
        </Badge>
      </div>

      {/* Lista de Propostas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Aguardando Revisão
          </CardTitle>
          <CardDescription>
            {isAdmin 
              ? 'Clique em "Revisar" para preencher os campos sensíveis e aprovar a proposta'
              : 'Lista dos seus dimensionamentos aguardando revisão do administrador'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : propostas.length === 0 ? (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertTitle>Nenhuma proposta aguardando revisão</AlertTitle>
              <AlertDescription>
                Todas as propostas foram revisadas ou não há propostas pendentes no momento.
              </AlertDescription>
            </Alert>
          ) : isMobile ? (
            // Layout de Cards para Mobile
            <div className="space-y-4">
              {propostas.map((proposta) => (
                <Card key={proposta.id} className="hover:bg-muted/50 transition-colors">
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-base truncate">{proposta.lead_nome}</h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {proposta.vendedor_nome}
                          </p>
                        </div>
                        {isAdmin && (
                          <Button 
                            onClick={() => handleRevisar(proposta)}
                            size="sm"
                            variant="default"
                            className="ml-2 flex-shrink-0"
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            <span className="hidden sm:inline">Revisar</span>
                          </Button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                        <div>
                          <p className="text-xs text-muted-foreground">Parceiro</p>
                          <p className="text-sm font-medium">{proposta.parceiro_nome}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Potência</p>
                          <p className="text-sm font-medium">
                            {proposta.potencia_total_paineis 
                              ? `${proposta.potencia_total_paineis.toLocaleString('pt-BR')} kWp` 
                              : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Valor Kits</p>
                          <p className="text-sm font-medium">
                            R$ {proposta.valor_total_kits?.toLocaleString('pt-BR', {
                              minimumFractionDigits: 2
                            }) || '0,00'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Enviado em</p>
                          <p className="text-sm font-medium">
                            {new Date(proposta.data_envio).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(proposta.data_envio).toLocaleTimeString('pt-BR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            // Layout de Tabela para Desktop
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Vendedor</TableHead>
                    <TableHead>Parceiro</TableHead>
                    <TableHead>Potência (kWp)</TableHead>
                    <TableHead>Valor Kits (R$)</TableHead>
                    <TableHead>Enviado em</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {propostas.map((proposta) => (
                    <TableRow key={proposta.id}>
                      <TableCell className="font-medium">{proposta.lead_nome}</TableCell>
                      <TableCell>{proposta.vendedor_nome}</TableCell>
                      <TableCell>{proposta.parceiro_nome}</TableCell>
                      <TableCell>
                        {proposta.potencia_total_paineis 
                          ? `${proposta.potencia_total_paineis.toLocaleString('pt-BR')} kWp` 
                          : '-'}
                      </TableCell>
                      <TableCell>
                        R$ {proposta.valor_total_kits?.toLocaleString('pt-BR', {
                          minimumFractionDigits: 2
                        }) || '0,00'}
                      </TableCell>
                      <TableCell>
                        {new Date(proposta.data_envio).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </TableCell>
                      <TableCell className="text-right">
                        {isAdmin && (
                          <Button 
                            onClick={() => handleRevisar(proposta)}
                            size="sm"
                            variant="default"
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            Revisar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Revisão */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={`${isMobile ? 'max-w-[95vw] w-full' : 'max-w-6xl'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className={isMobile ? "text-xl" : "text-2xl"}>
              {isAdmin ? 'Revisar Dimensionamento Completo' : 'Visualizar Dimensionamento'}
            </DialogTitle>
            <DialogDescription className={isMobile ? "text-sm" : ""}>
              {isAdmin 
                ? `Revisão detalhada de todas as etapas do dimensionamento de ${propostaSelecionada?.lead_nome}`
                : `Visualização detalhada do dimensionamento de ${propostaSelecionada?.lead_nome} (aguardando aprovação)`}
            </DialogDescription>
          </DialogHeader>

          {isLoadingDados ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-3 text-muted-foreground">Carregando dados...</span>
            </div>
          ) : dadosCompletos && (
            <Tabs defaultValue="etapa1" className="w-full">
              <TabsList className={`grid w-full ${isMobile ? 'grid-cols-2' : 'grid-cols-4'}`}>
                <TabsTrigger value="etapa1" className={`flex items-center gap-2 ${isMobile ? 'text-xs' : ''}`}>
                  <User className={isMobile ? "h-3 w-3" : "h-4 w-4"} />
                  <span className={isMobile ? "hidden sm:inline" : ""}>Etapa 1</span>
                  {isMobile && <span className="sm:hidden">1</span>}
                </TabsTrigger>
                <TabsTrigger value="etapa2" className={`flex items-center gap-2 ${isMobile ? 'text-xs' : ''}`}>
                  <Zap className={isMobile ? "h-3 w-3" : "h-4 w-4"} />
                  <span className={isMobile ? "hidden sm:inline" : ""}>Etapa 2</span>
                  {isMobile && <span className="sm:hidden">2</span>}
                </TabsTrigger>
                <TabsTrigger value="etapa3" className={`flex items-center gap-2 ${isMobile ? 'text-xs' : ''}`}>
                  <Package className={isMobile ? "h-3 w-3" : "h-4 w-4"} />
                  <span className={isMobile ? "hidden sm:inline" : ""}>Etapa 3</span>
                  {isMobile && <span className="sm:hidden">3</span>}
                </TabsTrigger>
                <TabsTrigger value="etapa4" className={`flex items-center gap-2 ${isMobile ? 'text-xs' : ''}`}>
                  <TrendingUp className={isMobile ? "h-3 w-3" : "h-4 w-4"} />
                  <span className={isMobile ? "hidden sm:inline" : ""}>Etapa 4</span>
                  {isMobile && <span className="sm:hidden">4</span>}
                </TabsTrigger>
              </TabsList>

              {/* ETAPA 1: Informações do Cliente */}
              <TabsContent value="etapa1" className="space-y-4">
              <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Informações do Cliente
                    </CardTitle>
                    <CardDescription>
                      Dados cadastrais e informações do lead
                    </CardDescription>
                </CardHeader>
                <CardContent className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                    {dadosCompletos.etapa1 && (
                      <>
                        {/* Coluna da Esquerda */}
                        <div className="space-y-4">
                          <div>
                            <Label className="text-sm text-muted-foreground">Nome</Label>
                            <p className="font-medium">{dadosCompletos.etapa1.nome || '-'}</p>
                          </div>
                          <div>
                            <Label className="text-sm text-muted-foreground">Telefone</Label>
                            <p className="font-medium">{formatarTelefone(dadosCompletos.etapa1.telefone)}</p>
                          </div>
                          <div>
                            <Label className="text-sm text-muted-foreground">Cidade/Estado</Label>
                            <p className="font-medium">{dadosCompletos.etapa1.cidade || '-'}/{dadosCompletos.etapa1.estado || '-'}</p>
                          </div>
                          <div>
                            <Label className="text-sm text-muted-foreground">Tipo de Telha</Label>
                            <p className="font-medium">{dadosCompletos.etapa1.tipo_telha || '-'}</p>
                          </div>
                          <div>
                            <Label className="text-sm text-muted-foreground">Padrão</Label>
                            <p className="font-medium">{dadosCompletos.etapa1.padrao || '-'}</p>
                          </div>
                        </div>

                        {/* Coluna da Direita */}
                        <div className="space-y-4">
                          <div>
                            <Label className="text-sm text-muted-foreground">Tipo</Label>
                            <p className="font-medium capitalize">{dadosCompletos.etapa1.tipo || '-'}</p>
                          </div>
                          <div>
                            <Label className="text-sm text-muted-foreground">Endereço</Label>
                            <p className="font-medium">{dadosCompletos.etapa1.endereco || '-'}, {dadosCompletos.etapa1.numero || 'S/N'}</p>
                          </div>
                          <div>
                            <Label className="text-sm text-muted-foreground">Tipo de Fixação</Label>
                            <p className="font-medium">{dadosCompletos.etapa1.tipo_fixacao || '-'}</p>
                          </div>
                          <div>
                            <Label className="text-sm text-muted-foreground">Grupo Tarifário</Label>
                            <p className="font-medium">{dadosCompletos.etapa1.grupo_tarifario || '-'}</p>
                          </div>
                          <div>
                            <Label className="text-sm text-muted-foreground">Origem</Label>
                            <p className="font-medium">{dadosCompletos.etapa1.origem || '-'}</p>
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Resumo de Dados de Consumo */}
                {dadosCompletos.dadosConsumo && (
              <Card>
                <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        Resumo de Consumo (Mensal)
                      </CardTitle>
                      <CardDescription>
                        Dados de consumo e despesas convertidos de anual para mensal
                      </CardDescription>
                </CardHeader>
                <CardContent className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                      <div>
                        <Label className="text-sm text-muted-foreground">Consumo Mensal</Label>
                        <p className="font-medium text-blue-600">
                          {dadosCompletos.dadosConsumo.consumo_anual 
                            ? `${(dadosCompletos.dadosConsumo.consumo_anual / 12).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh` 
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Despesa Energia Mensal</Label>
                        <p className="font-medium text-green-600">
                          {dadosCompletos.dadosConsumo.despesa_energia_anual 
                            ? `R$ ${(dadosCompletos.dadosConsumo.despesa_energia_anual / 12).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">COSIP Mensal</Label>
                        <p className="font-medium text-orange-600">
                          {dadosCompletos.dadosConsumo.despesa_cosip_anual 
                            ? `R$ ${(dadosCompletos.dadosConsumo.despesa_cosip_anual / 12).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Fatura Total Mensal</Label>
                        <p className="font-medium text-red-600">
                          {dadosCompletos.dadosConsumo.despesa_fatura_anual 
                            ? `R$ ${(dadosCompletos.dadosConsumo.despesa_fatura_anual / 12).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                            : '-'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* ETAPA 2: Dimensionamento do Sistema */}
              <TabsContent value="etapa2" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="h-5 w-5" />
                      Dimensionamento do Sistema
                    </CardTitle>
                    <CardDescription>
                      Cálculos e especificações técnicas do sistema fotovoltaico
                    </CardDescription>
                  </CardHeader>
                  <CardContent className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                    {dadosCompletos.etapa2 ? (
                      <>
                        <div>
                          <Label className="text-sm text-muted-foreground">Potência necessária</Label>
                    <p className="font-medium">
                            {dadosCompletos.etapa2.dimensio 
                              ? `${dadosCompletos.etapa2.dimensio.toLocaleString('pt-BR')} kWp` 
                              : '-'}
                    </p>
                  </div>
                  <div>
                          <Label className="text-sm text-muted-foreground">Eficiência</Label>
                    <p className="font-medium">
                            {dadosCompletos.etapa2.eficiencia 
                              ? `${dadosCompletos.etapa2.eficiencia.toLocaleString('pt-BR')}%` 
                              : '-'}
                    </p>
                  </div>
                  <div>
                          <Label className="text-sm text-muted-foreground">Geração Anual</Label>
                          <p className="font-medium">
                            {dadosCompletos.etapa2.geracao_anual_kwp 
                              ? `${dadosCompletos.etapa2.geracao_anual_kwp.toLocaleString('pt-BR')} kWh/kWp` 
                              : '-'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">Percentual de Geração</Label>
                          <p className="font-medium">
                            {dadosCompletos.etapa2.percentual 
                              ? `${dadosCompletos.etapa2.percentual.toLocaleString('pt-BR')}%` 
                              : '-'}
                          </p>
                        </div>
                      </>
                    ) : (
                      <div className="col-span-2">
                        <Alert>
                          <AlertCircle className="h-4 w-4" />
                          <AlertTitle>Dados não disponíveis</AlertTitle>
                          <AlertDescription>
                            Não foram encontrados dados de dimensionamento para esta proposta.
                          </AlertDescription>
                        </Alert>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ETAPA 3: Kits Selecionados */}
              <TabsContent value="etapa3" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Kits Selecionados
                    </CardTitle>
                    <CardDescription>
                      Equipamentos escolhidos para o sistema fotovoltaico
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {dadosCompletos.etapa3?.kits && dadosCompletos.etapa3.kits.length > 0 ? (
                      <>
                        <div className="space-y-3 mb-6">
                          {dadosCompletos.etapa3.kits.map((kit, index) => (
                            <div key={index} className="p-4 bg-muted/50 rounded-lg border">
                              <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                                {/* Coluna da Esquerda */}
                                <div className="space-y-3">
                                  <div>
                                    <Label className="text-xs text-muted-foreground">Código</Label>
                                    <p className="font-medium text-sm">{kit.codigo}</p>
                                  </div>
                                  <div>
                                    <Label className="text-xs text-muted-foreground">Potência</Label>
                                    <p className="font-medium text-sm">{kit.potencia ? `${kit.potencia} kWp` : 'N/A'}</p>
                                  </div>
                                  <div>
                                    <Label className="text-xs text-muted-foreground">Valor Total</Label>
                                    <p className="font-semibold text-sm text-blue-600">
                                      {kit.preco && kit.quantidade 
                                        ? `R$ ${(kit.preco * kit.quantidade).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                        : 'N/A'}
                                    </p>
                                  </div>
                                </div>

                                {/* Coluna da Direita */}
                                <div className="space-y-3">
                                  <div>
                                    <Label className="text-xs text-muted-foreground">Nome</Label>
                                    <p className="font-medium text-sm">{kit.nome || 'Kit sem nome'}</p>
                                  </div>
                                  <div>
                                    <Label className="text-xs text-muted-foreground">Quantidade</Label>
                                    <p className="font-medium text-sm">{kit.quantidade || 0} un.</p>
                                  </div>
                                  <div>
                                    <Label className="text-xs text-muted-foreground">Preço Unitário</Label>
                                    <p className="font-medium text-sm">
                                      {kit.preco 
                                        ? `R$ ${kit.preco.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                        : 'N/A'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className={`grid ${isMobile ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 md:grid-cols-4'} gap-4 pt-4 border-t`}>
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <Label className="text-xs text-blue-800">Valor Total dos Kits</Label>
                            <p className="font-bold text-lg text-blue-600">
                              R$ {dadosCompletos.etapa3.valor_total_kits.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div className="p-3 bg-green-50 rounded-lg">
                            <Label className="text-xs text-green-800">Potência Total</Label>
                            <p className="font-bold text-lg text-green-600">
                              {dadosCompletos.etapa3.potencia_total_paineis ? `${dadosCompletos.etapa3.potencia_total_paineis} kWp` : 'N/A'}
                            </p>
                          </div>
                          <div className="p-3 bg-purple-50 rounded-lg">
                            <Label className="text-xs text-purple-800">Total de Painéis</Label>
                            <p className="font-bold text-lg text-purple-600">
                              {dadosCompletos.etapa3.total_paineis} un.
                            </p>
                          </div>
                          <div className="p-3 bg-orange-50 rounded-lg">
                            <Label className="text-xs text-orange-800">R$/Wp (Kits)</Label>
                            <p className="font-bold text-lg text-orange-600">
                              R$ {dadosCompletos.etapa3.kits_r_wp?.toFixed(2) || '0,00'}
                            </p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Nenhum kit selecionado</AlertTitle>
                        <AlertDescription>
                          Não foram encontrados kits selecionados para esta proposta.
                        </AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ETAPA 4: Precificação e Aprovação */}
              <TabsContent value="etapa4" className="space-y-4">
                {/* Informações Gerais */}
                <Card>
                  <CardHeader>
                    <CardTitle className={isMobile ? "text-base" : "text-lg"}>Resumo da Proposta</CardTitle>
                  </CardHeader>
                  <CardContent className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-3'} gap-4`}>
                  <div>
                    <Label className="text-sm text-muted-foreground">Cliente</Label>
                    <p className="font-medium">{propostaSelecionada?.lead_nome}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Vendedor</Label>
                    <p className="font-medium">{propostaSelecionada?.vendedor_nome}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-muted-foreground">Parceiro</Label>
                    <p className="font-medium">{propostaSelecionada?.parceiro_nome || 'Não atribuído'}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Campos de Precificação */}
              <Card>
                <CardHeader>
                  <CardTitle className={`${isMobile ? 'text-base' : 'text-lg'} flex items-center gap-2`}>
                    <DollarSign className={isMobile ? "h-4 w-4" : "h-5 w-5"} />
                    Campos de Precificação
                  </CardTitle>
                  <CardDescription>
                      {isAdmin 
                        ? 'Preencha os valores para completar a precificação (campos obrigatórios *)'
                        : 'Valores aguardando aprovação do administrador (somente leitura)'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'} gap-4 pb-4 border-b`}>
                    <div>
                      <Label htmlFor="comissao_vendedor">Comissão Vendedor (%) *</Label>
                      <Input
                        id="comissao_vendedor"
                        type="text"
                        inputMode="decimal"
                        value={comissaoVendedor}
                        onChange={(e) => setComissaoVendedor(e.target.value)}
                        placeholder="4,00"
                        className="mt-1"
                        disabled={!isAdmin}
                      />
                    </div>
                    <div>
                      <Label htmlFor="comissao_parceiro">Comissão Parceiro (%) *</Label>
                      <Input
                        id="comissao_parceiro"
                        type="text"
                        inputMode="decimal"
                        value={comissaoParceiro}
                        onChange={(e) => setComissaoParceiro(e.target.value)}
                        placeholder="3,00"
                        className="mt-1"
                        disabled={!isAdmin}
                      />
                    </div>
                    {dadosCompletos.etapa1?.parceiro_pai_id && (
                      <div>
                        <Label htmlFor="comissao_parceiro_pai">Comissão Parceiro Pai (%) *</Label>
                        <Input
                          id="comissao_parceiro_pai"
                          type="text"
                          inputMode="decimal"
                          value={comissaoParceiroPai}
                          onChange={(e) => setComissaoParceiroPai(e.target.value)}
                          placeholder="1,00"
                          className="mt-1 bg-blue-50 border-blue-200"
                          disabled={!isAdmin}
                        />
                      </div>
                    )}
                    <div>
                      <Label htmlFor="integracao_r_wp">Integração (R$/Wp) *</Label>
                      <Input
                        id="integracao_r_wp"
                        type="text"
                        inputMode="decimal"
                        value={integracaoRwp}
                        onChange={(e) => setIntegracaoRwp(e.target.value)}
                        placeholder="0,9000"
                        className="mt-1"
                        disabled={!isAdmin}
                      />
                    </div>
                  </div>

                  <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-3'} gap-4`}>
                    <div>
                        <Label htmlFor="custo_materiais">Custo de Materiais (R$) *</Label>
                      <Input
                        id="custo_materiais"
                        type="text"
                        inputMode="decimal"
                        value={custoMateriais}
                        onChange={(e) => setCustoMateriais(e.target.value)}
                        placeholder="0,00"
                        className="mt-1"
                        disabled={!isAdmin}
                      />
                        {dadosCompletos.etapa4.total_paineis > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                            R$ {(parseFloat(custoMateriais.replace(',', '.')) / dadosCompletos.etapa4.total_paineis).toFixed(2)}/placa
                        </p>
                      )}
                    </div>

                    <div>
                        <Label htmlFor="impostos_percentual">Impostos (%) *</Label>
                      <Input
                        id="impostos_percentual"
                        type="text"
                        inputMode="decimal"
                        value={impostosPercentual}
                        onChange={(e) => setImpostosPercentual(e.target.value)}
                        placeholder="12,00"
                        className="mt-1"
                        disabled={!isAdmin}
                      />
                    </div>

                    <div>
                        <Label htmlFor="mao_obra_por_placa">Mão de Obra por Placa (R$) *</Label>
                      <Input
                        id="mao_obra_por_placa"
                        type="text"
                        inputMode="decimal"
                        value={maoObraPorPlaca}
                        onChange={(e) => setMaoObraPorPlaca(e.target.value)}
                        placeholder="0,00"
                        className="mt-1"
                        disabled={!isAdmin}
                      />
                        {dadosCompletos.etapa4.total_paineis > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                            Total: R$ {(parseFloat(maoObraPorPlaca.replace(',', '.')) * dadosCompletos.etapa4.total_paineis).toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Campos de Estrutura de Solo/Carport - Condicionais */}
                  {(dadosCompletos.etapa1?.tipo_fixacao === 'Solo' ||
                    dadosCompletos.etapa1?.tipo_fixacao === 'Carport') && (
                    <div className="space-y-4 rounded-lg border-t bg-blue-50/50 p-4 pt-4">
                      {dadosCompletos.etapa1?.tipo_fixacao === 'Solo' && (
                        <div className="flex flex-col gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                          <Label
                            htmlFor="revisao_incluir_custos_estrutura"
                            className="cursor-pointer text-sm font-medium leading-snug"
                          >
                            Incluir custos de estrutura e fundação?
                          </Label>
                          <Switch
                            id="revisao_incluir_custos_estrutura"
                            checked={incluirCustosEstruturaFundacao}
                            disabled={!isAdmin}
                            onCheckedChange={(checked) => {
                              const incluir = Boolean(checked);
                              setIncluirCustosEstruturaFundacao(incluir);
                              const totalPaineis = dadosCompletos.etapa4?.total_paineis || 0;
                              if (!incluir) {
                                setValorEstruturaMetallica('0');
                                setValorFundacao('0');
                                setValorEquipamentosAlugados('0');
                              } else {
                                const ve = 220 * totalPaineis;
                                const vf = 15 * totalPaineis;
                                const veq = Math.max(14 * totalPaineis, 500);
                                setValorEstruturaMetallica(ve.toString());
                                setValorFundacao(vf.toString());
                                setValorEquipamentosAlugados(veq.toString());
                              }
                            }}
                          />
                        </div>
                      )}
                      {(dadosCompletos.etapa1?.tipo_fixacao === 'Carport' ||
                        (dadosCompletos.etapa1?.tipo_fixacao === 'Solo' && incluirCustosEstruturaFundacao)) && (
                        <div
                          className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-3'} gap-4 rounded-lg bg-blue-50/50 p-2`}
                        >
                          <div>
                            <Label htmlFor="valor_estrutura_metallica">Valor da Estrutura Metálica (R$) *</Label>
                            <Input
                              id="valor_estrutura_metallica"
                              type="text"
                              inputMode="decimal"
                              value={valorEstruturaMetallica}
                              onChange={(e) => setValorEstruturaMetallica(e.target.value)}
                              placeholder="0,00"
                              className="mt-1"
                              disabled={!isAdmin}
                            />
                            {dadosCompletos.etapa4.total_paineis > 0 && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                R${' '}
                                {(
                                  parseFloat(valorEstruturaMetallica.replace(',', '.')) /
                                  dadosCompletos.etapa4.total_paineis
                                ).toFixed(2)}
                                /placa
                              </p>
                            )}
                            {dadosCompletos.etapa1?.tipo_fixacao === 'Solo' &&
                              dadosCompletos.etapa4.total_paineis > 0 && (
                                <p className="mt-1 text-xs text-blue-600">
                                  R$ 220,00 por painel (calculado automaticamente)
                                </p>
                              )}
                            {dadosCompletos.etapa1?.tipo_fixacao === 'Carport' &&
                              dadosCompletos.etapa4.total_paineis > 0 && (
                                <p className="mt-1 text-xs text-blue-600">
                                  R$ 500,00 por painel (calculado automaticamente)
                                </p>
                              )}
                          </div>

                          <div>
                            <Label htmlFor="valor_fundacao">Valor da Fundação (R$) *</Label>
                            <Input
                              id="valor_fundacao"
                              type="text"
                              inputMode="decimal"
                              value={valorFundacao}
                              onChange={(e) => setValorFundacao(e.target.value)}
                              placeholder="0,00"
                              className="mt-1"
                              disabled={!isAdmin}
                            />
                            {dadosCompletos.etapa4.total_paineis > 0 && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                R${' '}
                                {(
                                  parseFloat(valorFundacao.replace(',', '.')) /
                                  dadosCompletos.etapa4.total_paineis
                                ).toFixed(2)}
                                /placa
                              </p>
                            )}
                            {dadosCompletos.etapa1?.tipo_fixacao === 'Solo' &&
                              dadosCompletos.etapa4.total_paineis > 0 && (
                                <p className="mt-1 text-xs text-blue-600">
                                  R$ 15,00 por painel (calculado automaticamente)
                                </p>
                              )}
                            {dadosCompletos.etapa1?.tipo_fixacao === 'Carport' &&
                              dadosCompletos.etapa4.total_paineis > 0 && (
                                <p className="mt-1 text-xs text-blue-600">
                                  R$ 22,00 por painel (mínimo R$ 2.000,00)
                                </p>
                              )}
                            {dadosCompletos.etapa1?.tipo_fixacao === 'Carport' &&
                              parseFloat(valorFundacao.replace(',', '.')) > 0 &&
                              parseFloat(valorFundacao.replace(',', '.')) < 2000 && (
                                <p className="mt-1 flex items-center gap-1 text-xs text-amber-600">
                                  <span>⚠️</span>
                                  Valor abaixo do mínimo recomendado (R$ 2.000,00)
                                </p>
                              )}
                          </div>

                          <div>
                            <Label htmlFor="valor_equipamentos_alugados">Equipamentos Alugados (R$) *</Label>
                            <Input
                              id="valor_equipamentos_alugados"
                              type="text"
                              inputMode="decimal"
                              value={valorEquipamentosAlugados}
                              onChange={(e) => setValorEquipamentosAlugados(e.target.value)}
                              placeholder="0,00"
                              className="mt-1"
                              disabled={!isAdmin}
                            />
                            {dadosCompletos.etapa4.total_paineis > 0 && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                R${' '}
                                {(
                                  parseFloat(valorEquipamentosAlugados.replace(',', '.')) /
                                  dadosCompletos.etapa4.total_paineis
                                ).toFixed(2)}
                                /placa
                              </p>
                            )}
                            {dadosCompletos.etapa1?.tipo_fixacao === 'Solo' &&
                              dadosCompletos.etapa4.total_paineis > 0 && (
                                <p className="mt-1 text-xs text-blue-600">
                                  R$ 14,00 por painel (mínimo R$ 500,00)
                                </p>
                              )}
                            {dadosCompletos.etapa1?.tipo_fixacao === 'Carport' &&
                              dadosCompletos.etapa4.total_paineis > 0 && (
                                <p className="mt-1 text-xs text-blue-600">
                                  R$ 14,00 por painel (mínimo R$ 500,00)
                                </p>
                              )}
                            {parseFloat(valorEquipamentosAlugados.replace(',', '.')) > 0 &&
                              parseFloat(valorEquipamentosAlugados.replace(',', '.')) < 500 && (
                                <p className="mt-1 flex items-center gap-1 text-xs text-amber-600">
                                  <span>⚠️</span>
                                  Valor abaixo do mínimo recomendado (R$ 500,00)
                                </p>
                              )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Preview dos Cálculos */}
              {preview && (
                <Card>
                  <CardHeader>
                    <CardTitle className={`${isMobile ? 'text-base' : 'text-lg'} flex items-center gap-2`}>
                      <Calculator className={isMobile ? "h-4 w-4" : "h-5 w-5"} />
                      Preview dos Resultados
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={`grid ${isMobile ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 md:grid-cols-4'} gap-4`}>
                      <div className="p-4 bg-blue-600 rounded-lg border border-blue-700">
                        <Label className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-white/80`}>Preço do Projeto</Label>
                        <div className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-white mt-1`}>
                          R$ {preview.preco_projeto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </div>
                      </div>

                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <Label className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-blue-800`}>Preço Integração</Label>
                        <div className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-blue-600 mt-1`}>
                          R$ {preview.preco_integracao.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </div>
                      </div>

                      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <Label className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-red-800`}>Impostos</Label>
                        <div className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-red-600 mt-1`}>
                          R$ {preview.impostos_valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </div>
                      </div>

                      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <Label className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-red-800`}>Mão de Obra</Label>
                        <div className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-red-600 mt-1`}>
                          R$ {preview.mao_obra_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </div>
                      </div>

                      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <Label className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-red-800`}>Custo Total Integração</Label>
                        <div className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-red-600 mt-1`}>
                          R$ {preview.custo_total_integracao.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </div>
                      </div>

                      <div className={`p-4 rounded-lg border ${
                        preview.lucro_liquido >= 0 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <Label className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium ${
                          preview.lucro_liquido >= 0 ? 'text-green-800' : 'text-red-800'
                        }`}>
                          Lucro Líquido
                        </Label>
                        <div className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold mt-1 ${
                          preview.lucro_liquido >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          R$ {preview.lucro_liquido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </div>
                      </div>

                      <div className={`p-4 rounded-lg border ${
                        preview.lucro_percentual >= 0 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <Label className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium ${
                          preview.lucro_percentual >= 0 ? 'text-green-800' : 'text-red-800'
                        }`}>
                          Lucro (%)
                        </Label>
                        <div className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold mt-1 ${
                          preview.lucro_percentual >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {preview.lucro_percentual.toFixed(2)}%
                        </div>
                      </div>
                    </div>

                    {preview.lucro_liquido < 0 && (
                      <Alert variant="destructive" className="mt-4">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Atenção: Prejuízo</AlertTitle>
                        <AlertDescription>
                          Os valores configurados resultam em prejuízo. Revise os campos antes de aprovar.
                        </AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className={isMobile ? "flex-col gap-2 sm:flex-row" : ""}>
            <Button
              variant="outline"
              onClick={() => {
                setDialogOpen(false);
                setPropostaSelecionada(null);
                setDadosCompletos(null);
              }}
              disabled={isAprovando}
              className={isMobile ? "w-full sm:w-auto" : ""}
            >
              {isAdmin ? 'Cancelar' : 'Fechar'}
            </Button>
            {isAdmin && (
              <Button
                onClick={handleAprovar}
                disabled={isAprovando || isLoadingDados}
                className={`bg-green-600 hover:bg-green-700 ${isMobile ? "w-full sm:w-auto" : ""}`}
              >
                {isAprovando ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Aprovando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Aprovar Revisão
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
